"use client";

import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useCategoryTree } from "@/hooks/api/useCategoryTree";
import { flattenCategories } from "@/lib/categories";
import { api } from "@/lib/axios";
import { buttonVariants } from "@/components/ui/button";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export default function ProductCategoriesPage() {
  const qc = useQueryClient();
  const { data: tree = [], isLoading, isError, error } = useCategoryTree();
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState("");

  const rows = useMemo(() => flattenCategories(tree), [tree]);

  const createMut = useMutation({
    mutationFn: async () => {
      const body: { name: string; parent_id?: number; is_active: number } = {
        name: name.trim(),
        is_active: 1,
      };
      if (parentId !== "") {
        body.parent_id = Number(parentId);
      }
      await api.post("/api/categories", body);
    },
    onSuccess: async () => {
      setName("");
      setParentId("");
      await qc.invalidateQueries({ queryKey: ["categories"] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/categories/${id}`);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["categories"] });
    },
  });

  const errMsg = isError
    ? axios.isAxiosError(error)
      ? (error.response?.data as { error?: string } | undefined)?.error ?? error.message
      : "Failed to load categories."
    : null;

  return (
    <div className="space-y-6 p-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Product categories</h1>
          <p className="text-sm text-muted-foreground">
            Used in product catalog and POS filters.{" "}
            <Link to="/products" className={cn(buttonVariants({ variant: "link", size: "sm" }), "h-auto p-0")}>
              Back to products
            </Link>
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Add category</CardTitle>
          <CardDescription>Root category uses empty parent. Delete blocked while subcategories exist.</CardDescription>
        </CardHeader>
        <CardContent className="grid max-w-lg gap-3">
          <div className="grid gap-2">
            <Label htmlFor="cat-name">Name</Label>
            <Input
              id="cat-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Grocery"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="cat-parent">Parent (optional)</Label>
            <select
              id="cat-parent"
              className="flex h-9 w-full rounded-lg border border-input bg-background px-2 text-sm"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
            >
              <option value="">— Root —</option>
              {rows.map((r) => (
                <option key={r.id} value={String(r.id)}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <Button
            type="button"
            disabled={createMut.isPending || name.trim() === ""}
            onClick={() => createMut.mutate()}
          >
            {createMut.isPending ? "Saving…" : "Create category"}
          </Button>
          {createMut.isError && (
            <p className="text-sm text-destructive" role="alert">
              {(createMut.error as { response?: { data?: { error?: string } } })?.response?.data?.error ??
                "Could not create category."}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">All categories</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
          {errMsg && <p className="text-sm text-destructive">{errMsg}</p>}
          {!isLoading && !errMsg && rows.length === 0 && (
            <p className="text-sm text-muted-foreground">No categories yet — add one above.</p>
          )}
          {!isLoading && !errMsg && rows.length > 0 && (
            <ul className="divide-y rounded-lg border">
              {rows.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                  <span>{r.label}</span>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    disabled={deleteMut.isPending}
                    onClick={() => {
                      if (!window.confirm(`Delete category “${r.label.replace(/^—+\s*/, "")}”?`)) {
                        return;
                      }
                      deleteMut.mutate(r.id, {
                        onError: (e) => {
                          const msg = axios.isAxiosError(e)
                            ? (e.response?.data as { error?: string } | undefined)?.error
                            : null;
                          window.alert(msg ?? "Delete failed.");
                        },
                      });
                    }}
                  >
                    Delete
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
